import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PIN_CATEGORY } from "@/lib/utils";
import {
  X, Check, Trash2, Edit3, ChevronDown, Loader2, Link2,
} from "lucide-react";
import ObjectRelations from "./object/ObjectRelations";

const STATUSES = ["open", "in_progress", "pending_verification", "corrected", "closed"];
const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  pending_verification: "Pending Verify",
  corrected: "Corrected",
  closed: "Closed",
};
const STATUS_COLOR = {
  open: "var(--st-open)",
  in_progress: "var(--st-assigned)",
  pending_verification: "var(--st-assigned)",
  corrected: "var(--st-corrected)",
  closed: "var(--fop-text-3)",
};
const PRIORITIES = ["low", "medium", "high", "critical"];
const PRIORITY_LABELS = {
  low: "Low", medium: "Med", high: "High", critical: "Crit",
};

/**
 * PinQuickActions — the inline pin-edit popover.
 *
 * Anchored next to a pin on the drawing. Lets the field user:
 *   - flip status with one tap (Open → In progress → Corrected → Closed)
 *   - change priority
 *   - rename the title (inline)
 *   - delete (own pins, no linked workflows)
 *
 * Per the strategic directive: NO sidebar trip, NO full page, NO software
 * feeling. Operational marking-up-reality speed.
 */
export default function PinQuickActions({ pin, onChanged, onClose, onDeleted }) {
  const popRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(pin.title || "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const [showRelated, setShowRelated] = useState(false);

  useEffect(() => { setDraftTitle(pin.title || ""); }, [pin.id, pin.title]);

  // Click outside → close (but ignore clicks on the pin marker itself).
  useEffect(() => {
    const onDocClick = (e) => {
      if (!popRef.current) return;
      if (popRef.current.contains(e.target)) return;
      const target = e.target;
      if (target?.closest && target.closest('[data-testid^="pin-marker-"]')) return;
      onClose?.();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [onClose]);

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cat = PIN_CATEGORY[pin.category] || {};
  const pending = pin._pending_sync;

  const patch = async (body) => {
    setBusy(true); setError(null);
    try {
      if (pending) {
        // Pin still queued offline — let the queue carry the update.
        onChanged?.();
        return;
      }
      await api.patch(`/pins/${pin.id}`, body);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const saveTitle = async () => {
    if (!draftTitle.trim() || draftTitle.trim() === pin.title) {
      setEditingTitle(false);
      return;
    }
    await patch({ title: draftTitle.trim() });
    setEditingTitle(false);
  };

  const doDelete = async () => {
    if (pending) {
      setError("Pin still syncing — try again in a moment.");
      return;
    }
    setBusy(true); setError(null);
    try {
      await api.delete(`/pins/${pin.id}`);
      onDeleted?.(pin.id);
      onChanged?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div
      ref={popRef}
      className="rounded-md shadow-2xl border bg-[var(--fop-surface)] backdrop-blur-sm overflow-hidden"
      style={{ width: 260, borderColor: cat.color || "var(--fop-border)" }}
      onClick={(e) => e.stopPropagation()}
      data-testid="pin-quick-actions"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-2.5 py-2 border-b" style={{ borderColor: "var(--fop-border)" }}>
        <div className="w-1 self-stretch rounded-full shrink-0" style={{ background: cat.color || "var(--fop-primary)" }} />
        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <input
              autoFocus
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); saveTitle(); }
                if (e.key === "Escape") { setEditingTitle(false); setDraftTitle(pin.title || ""); }
              }}
              className="w-full text-sm font-medium px-1 py-0.5"
              data-testid="pin-edit-title-input"
            />
          ) : (
            <button
              onClick={() => setEditingTitle(true)}
              className="text-left w-full text-sm text-[var(--fop-text)] font-medium truncate hover:text-[var(--fop-primary)] flex items-center gap-1 group"
              data-testid="pin-edit-title-trigger"
              title="Click to rename"
            >
              <span className="truncate">{pin.title || "Untitled pin"}</span>
              <Edit3 size={10} className="opacity-0 group-hover:opacity-60 shrink-0" />
            </button>
          )}
          <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
            {cat.label || pin.category}
            {pending && <span className="ml-2 text-[var(--st-assigned)]">· pending sync</span>}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] p-1 -mr-1"
          aria-label="Close"
          data-testid="pin-quick-close"
        >
          <X size={12} />
        </button>
      </div>

      {/* Status row — primary action */}
      <div className="px-2.5 pt-2 pb-1.5">
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1.5">Status</div>
        <div className="flex flex-wrap gap-1">
          {STATUSES.map((s) => {
            const active = pin.status === s;
            return (
              <button
                key={s}
                onClick={() => patch({ status: s })}
                disabled={busy || active}
                className="text-[11px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors disabled:opacity-100"
                style={{
                  background: active ? STATUS_COLOR[s] : "var(--fop-surface-2)",
                  color: active ? "#0a0a0b" : "var(--fop-text-2)",
                  fontWeight: active ? 700 : 500,
                }}
                data-testid={`pin-status-${s}`}
              >
                {STATUS_LABELS[s]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Priority row */}
      <div className="px-2.5 py-1.5">
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1.5">Priority</div>
        <div className="flex gap-1">
          {PRIORITIES.map((p) => {
            const active = pin.priority === p;
            return (
              <button
                key={p}
                onClick={() => patch({ priority: p })}
                disabled={busy || active}
                className={`flex-1 text-[11px] font-mono uppercase tracking-wider py-0.5 rounded transition-colors ${
                  active
                    ? "bg-[var(--fop-text)] text-[var(--fop-bg)] font-bold"
                    : "bg-[var(--fop-surface-2)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)]"
                } disabled:opacity-100`}
                data-testid={`pin-priority-${p}`}
              >
                {PRIORITY_LABELS[p]}
              </button>
            );
          })}
        </div>
      </div>

      {/* R11 (2026-09-10) — collapsed by default so the fast status/
          priority flow this popover exists for stays uncluttered; a
          field user who wants context can pull it open. */}
      {!pending && (
        <div className="px-2.5 py-1.5 border-t" style={{ borderColor: "var(--fop-border)" }}>
          <button
            type="button"
            onClick={() => setShowRelated((v) => !v)}
            className="w-full flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            data-testid="pin-related-toggle"
          >
            <Link2 size={10} />
            Related objects
            <ChevronDown size={11} className={`ml-auto transition-transform ${showRelated ? "rotate-180" : ""}`} />
          </button>
          {showRelated && (
            <div className="mt-2" data-testid="pin-related-content">
              <ObjectRelations objectType="pin" objectId={pin.id} />
            </div>
          )}
        </div>
      )}

      {/* Footer: delete + comment count */}
      <div className="flex items-center justify-between px-2.5 py-2 border-t" style={{ borderColor: "var(--fop-border)" }}>
        <div className="text-[10px] font-mono text-[var(--fop-text-3)]">
          {pin.workflow_ids?.length > 0 && (
            <span>· {pin.workflow_ids.length} workflow{pin.workflow_ids.length === 1 ? "" : "s"}</span>
          )}
        </div>
        {confirmDelete ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              data-testid="pin-delete-cancel"
            >
              Cancel
            </button>
            <button
              onClick={doDelete}
              disabled={busy}
              className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-[var(--st-escalated)] text-white flex items-center gap-1 disabled:opacity-50"
              data-testid="pin-delete-confirm"
            >
              {busy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
              Delete
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] flex items-center gap-1"
            data-testid="pin-delete-trigger"
          >
            <Trash2 size={10} /> Delete
          </button>
        )}
      </div>

      {error && (
        <div className="px-2.5 py-1.5 text-[10px] text-[var(--st-escalated)] border-t border-[var(--fop-border)]" data-testid="pin-quick-error">
          {error}
        </div>
      )}
    </div>
  );
}
